import { useApolloClient } from '@apollo/client';
import * as React from 'react';

import { useBankConnectionSync } from '../../hooks/useBankConnectionSync';
import { AlertTriangleIcon, InfoCircleIcon, RefreshIcon } from '../../icons';
import logger from '../../lib/logger';

interface IBaseSyncingNoticeProps {
  /**
   * Whether connections are actively syncing (no lastSyncedAt yet).
   */
  hasUnsyncedConnections: boolean;
  /**
   * Whether historical sync is still in progress.
   */
  hasIncompleteHistoricalSync: boolean;
  /**
   * Whether there is a potential sync issue.
   */
  hasPotentialSyncIssue: boolean;
}

/**
 * Base visual component for displaying a syncing notice.
 * Use this component directly when you need to control the syncing state manually.
 */
export const BaseSyncingNotice = ({
  hasUnsyncedConnections,
  hasIncompleteHistoricalSync,
  hasPotentialSyncIssue,
}: IBaseSyncingNoticeProps) => {
  if (!hasUnsyncedConnections && !hasIncompleteHistoricalSync && !hasPotentialSyncIssue) {
    return null;
  }

  // Show different copy based on the sync state
  if (hasPotentialSyncIssue) {
    return (
      <div className="bg-utility-error-50 ring-utility-error-200 flex items-center gap-2 rounded-xl px-6 py-5 ring-1 ring-inset">
        <AlertTriangleIcon className="text-utility-error-700 size-6 shrink-0" />
        <span className="text-utility-error-700 text-base font-medium">
          Plaid is having difficulty sending your financial data. We're working on it and will update you by email with
          a solution.
        </span>
      </div>
    );
  }

  if (hasUnsyncedConnections) {
    return (
      <div className="bg-utility-orange-50 ring-utility-orange-200 flex items-center gap-2 rounded-xl px-6 py-5 ring-1 ring-inset">
        <RefreshIcon className="text-utility-orange-700 size-6 shrink-0 animate-spin" />
        <span className="text-utility-orange-700 text-base font-medium">
          Plaid is sending your financial information.
        </span>
      </div>
    );
  }

  if (hasIncompleteHistoricalSync) {
    return (
      <div className="bg-utility-warning-50 ring-utility-warning-200 flex items-center gap-2 rounded-xl px-6 py-5 ring-1 ring-inset">
        <InfoCircleIcon className="text-utility-warning-700 size-6 shrink-0" />
        <span className="text-utility-warning-700 text-base font-medium">
          Plaid is sending your historical transactions. This may take up to 24 hours. During that time, your product
          experience will be incomplete.
        </span>
      </div>
    );
  }

  return null;
};

/**
 * Connected component that uses the useBankConnectionSync hook to determine syncing state.
 * Au
 * tomatically refreshes the Apollo client store when syncing starts.
 */
const SyncingNotice = () => {
  const { hasUnsyncedConnections, hasIncompleteHistoricalSync, hasPotentialSyncIssue } = useBankConnectionSync();
  const client = useApolloClient();
  const prevUnsyncedRef = React.useRef(hasUnsyncedConnections);
  const prevHistoricalRef = React.useRef(hasIncompleteHistoricalSync);

  // Automatically refresh store when either flag transitions from true to false
  React.useEffect(() => {
    const unsyncedStarted = prevUnsyncedRef.current && !hasUnsyncedConnections;
    const historicalStarted = prevHistoricalRef.current && !hasIncompleteHistoricalSync;

    if (unsyncedStarted || historicalStarted) {
      // Refresh store to make sure we have fresh data
      client.resetStore().catch(() => {
        logger.error('Failed to reset Apollo client store');
      });
    }

    prevUnsyncedRef.current = hasUnsyncedConnections;
    prevHistoricalRef.current = hasIncompleteHistoricalSync;
  }, [hasUnsyncedConnections, hasIncompleteHistoricalSync, client]);

  return (
    <BaseSyncingNotice
      hasUnsyncedConnections={hasUnsyncedConnections}
      hasIncompleteHistoricalSync={hasIncompleteHistoricalSync}
      hasPotentialSyncIssue={hasPotentialSyncIssue}
    />
  );
};

export default SyncingNotice;
